import { useCallback } from 'react';
import { useFormatDate } from '@app/datetime';
import { Assets } from '@app/ui';
import useBuildContentstackImageSrc from '@drc/hooks/useBuildContentstackImageSrc';
import { SellerPromotionValueType } from '../consts';
import type { SellerPromotion, InboxMessageFields } from '../types';

const logoPath = '/message-center/seller-promotion-icon.svg';

/**
 * Convert the seller promotion data to message template structure
 */
const useSellerPromoToMsgConverter = () => {
  const formatDataHandle = useFormatDate();
  const formatDate = useCallback(
    (date) => formatDataHandle(date, 'date_and_time'),
    [formatDataHandle],
  );

  const { imageSrc: imageUrl } = useBuildContentstackImageSrc({
    src: Assets.getUrl(logoPath),
    disableSrcSets: true,
    options: {
      width: 56,
      height: 56,
    },
  });

  return useCallback(
    (sellerPromotion: SellerPromotion): InboxMessageFields | null => {
      if (!sellerPromotion) {
        return null;
      }

      const curTime = new Date();
      const expireTime = new Date(sellerPromotion.details?.expiresAt);
      const activateTime = new Date(sellerPromotion.details?.activatesAt);

      if (
        sellerPromotion.details?.expiresAt &&
        sellerPromotion.details?.expiresAt !== '0001-01-01T00:00:00Z' &&
        curTime > expireTime
      ) {
        return null;
      }

      if (sellerPromotion.deletedAt) {
        return null;
      }

      const value = sellerPromotion.details?.value?.value;
      const type = sellerPromotion.details?.value?.type;

      if (!value) {
        return null;
      }

      let subtitle = 'Offer is';

      if (type === SellerPromotionValueType.FlatRate) {
        subtitle += ` $${value} off seller fees`;
      }

      if (type === SellerPromotionValueType.RateDiscount) {
        subtitle += ` ${+value * 100}% off seller fees`;
      }

      if (type === SellerPromotionValueType.ReplacementRate) {
        subtitle += ` ${+value * 100}% as the seller fees`;
      }

      if (sellerPromotion.details?.minPrice) {
        subtitle += ` limited to a minimum sale price of $${sellerPromotion.details?.minPrice}`;
      }

      if (
        sellerPromotion.details?.activatesAt &&
        sellerPromotion.details?.activatesAt !== '0001-01-01T00:00:00Z' &&
        activateTime > curTime
      ) {
        if (
          sellerPromotion.details?.expiresAt &&
          sellerPromotion.details?.expiresAt !== '0001-01-01T00:00:00Z'
        ) {
          subtitle += ` on sales made between ${formatDate(
            sellerPromotion.details?.activatesAt,
          )} and ${formatDate(sellerPromotion.details?.expiresAt)}`;
        } else {
          subtitle += ` on sales made after ${formatDate(
            sellerPromotion.details?.activatesAt,
          )}`;
        }
      } else if (
        sellerPromotion.details?.expiresAt &&
        sellerPromotion.details?.expiresAt !== '0001-01-01T00:00:00Z'
      ) {
        subtitle += ` on sales made before ${formatDate(
          sellerPromotion.details?.expiresAt,
        )}`;
      }

      if (sellerPromotion.eligibilityRequirements?.verticals?.length > 0) {
        subtitle += `. This is applicable only for ${sellerPromotion.eligibilityRequirements?.verticals.join(
          ', ',
        )}`;

        if (
          sellerPromotion.eligibilityRequirements?.countryIso2Codes?.length > 0
        ) {
          subtitle += ` and eligible only from ${sellerPromotion.eligibilityRequirements?.countryIso2Codes.join(
            ', ',
          )}`;
        }
      } else if (
        sellerPromotion.eligibilityRequirements?.countryIso2Codes?.length > 0
      ) {
        subtitle += `. This is eligible only from ${sellerPromotion.eligibilityRequirements?.countryIso2Codes.join(
          ', ',
        )}`;
      }

      if (
        sellerPromotion.eligibilityRequirements?.products &&
        sellerPromotion.eligibilityRequirements.products?.length > 0
      ) {
        subtitle += `. This is for selected products only`;
      }

      subtitle += '.';

      return {
        node: {
          id: sellerPromotion.id,
          deliveredAt: sellerPromotion.createdAt || '',
          title: 'Seller Fee Promo Offer',
          subtitle,
          isRead: false,
          imageUrl,
          link: '',
        },
      };
    },
    [formatDate, imageUrl],
  );
};

export default useSellerPromoToMsgConverter;
